import type { ReactNode } from 'react';
import { LogoLockup, LogoMark } from '@/components/Logo';

interface AuthSplitLayoutProps {
  /** Large headline on the brand panel, e.g. "Your restaurants, one console". */
  headline: ReactNode;
  /** One or two sentences under the headline. */
  blurb: ReactNode;
  /** Three or so selling points. Kept short — they are read at a glance. */
  points: string[];
  /** Heading above the form. Says what this sign-in is FOR. */
  title: string;
  /** Supporting line under the form heading. */
  subtitle: string;
  /** The form itself, plus any footer links. */
  children: ReactNode;
}

/**
 * The two-panel sign-in shell shared by /admin/login and /portal/login.
 *
 * Layout: brand panel left, form right. The panel is the logo's own navy
 * (`bg-navy`) carrying the reversed lockup, so the artwork needs no plate. On
 * screens narrower than `lg` the panel is dropped entirely rather than stacked
 * — a tall marketing block above a login form just pushes the fields off a
 * phone screen — and the form grows a compact header with the normal lockup so
 * the brand is still present.
 *
 * Both logins share this shell deliberately: they are the same product, and the
 * only thing that should differ is the copy.
 */
export function AuthSplitLayout({
  headline,
  blurb,
  points,
  title,
  subtitle,
  children,
}: AuthSplitLayoutProps) {
  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel. aria-hidden: it is promotional copy, and every fact a
          screen-reader user needs to sign in is in the form panel. */}
      <section
        aria-hidden="true"
        // 60/40 against the form panel. No max-width cap: one would silently
        // win over the percentage on wide screens and drift the split back
        // toward 50/50, which is what it used to do.
        className="relative hidden w-[60%] shrink-0 select-none overflow-hidden bg-navy lg:flex lg:flex-col"
      >
        {/* Depth without imagery: one off-centre glow plus concentric rings
            echoing the compass in the mark. Pure CSS, so nothing extra to
            download, and all of it sits under the content. */}
        <div
          className="pointer-events-none absolute -right-1/4 top-[-15%] h-[70rem] w-[70rem] rounded-full opacity-[0.55]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(22,64,110,0.95), rgba(15,46,80,0) 70%)',
          }}
        />
        <div className="pointer-events-none absolute -left-32 bottom-[-18rem] h-[46rem] w-[46rem] rounded-full border border-white/[0.07]" />
        <div className="pointer-events-none absolute -left-16 bottom-[-14rem] h-[34rem] w-[34rem] rounded-full border border-white/[0.05]" />

        {/* Centred, but it must never clip. At this type size the block needs
            ~680px of height, and a 1366x768 laptop only gives the viewport about
            660 — with the panel's overflow-hidden that would silently cut the
            logo off. So: generous padding only when there is vertical room (a
            HEIGHT query — `xl:` is width and 1366x768 would wrongly qualify),
            and the column scrolls as a last resort instead of clipping.
            min-h-full on the inner box keeps it centred while still being able
            to grow past the viewport. */}
        <div className="relative flex-1 overflow-y-auto">
          <div className="flex min-h-full flex-col items-center justify-center px-12 py-12 text-center xl:px-16 [@media(min-height:860px)]:py-20">
            {/* items-center above, not the default stretch — besides centring, it
                is what stops an auto-width image being pulled to the full panel
                width and squashed flat. */}
            <LogoLockup className="h-40 xl:h-48" reversed />

            <div className="mt-12 max-w-2xl">
              <h2 className="text-[2.5rem] font-bold leading-[1.12] tracking-tight text-white xl:text-[3rem]">
                {headline}
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-[17px] leading-relaxed text-white/70 xl:text-lg">
                {blurb}
              </p>

              {/* Centred as a block, but the items themselves stay left-aligned:
                  centring each line individually would stagger the ticks and the
                  list would read as ragged rather than deliberate. */}
              <ul className="mt-11 inline-flex flex-col gap-4 text-left">
                {points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-base text-white/85">
                    {/* Amber tick: the logo's accent, used here and nowhere else,
                        so it stays an accent instead of becoming a second theme. */}
                    <svg
                      viewBox="0 0 20 20"
                      className="mt-0.5 h-5 w-5 shrink-0 text-accent"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 10.5l4 4 8-9" />
                    </svg>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* A flex sibling of the scroll area rather than absolutely positioned,
            so it can never end up sitting on top of the centred block. */}
        <p className="relative shrink-0 px-12 pb-10 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Order Pilot. All rights reserved.
        </p>
      </section>

      {/* Form panel */}
      <main className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
        <div className="w-full max-w-sm">
          {/* Stands in for the hidden brand panel on small screens. */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <LogoMark className="h-10 w-10" />
            <div>
              <p className="text-sm font-semibold leading-tight text-slate-900">Order Pilot</p>
              <p className="text-xs leading-tight text-slate-500">Cloud</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{subtitle}</p>

          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
